import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const KEY = "sangu_cart_v1";

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => load()); // { [producto_id]: { producto_id, nombre, precio, cantidad } }

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (err) {
      console.warn("No se pudo persistir el carrito:", err);
    }
  }, [items]);

  const add = useCallback((producto, qty = 1) => {
    setItems((prev) => {
      const existing = prev[producto.id];
      const cantidad = (existing?.cantidad || 0) + qty;
      return {
        ...prev,
        [producto.id]: {
          producto_id: producto.id,
          nombre: producto.nombre,
          precio: Number(producto.precio),
          imagen_path: producto.imagen_path || "",
          cantidad: Math.max(0, cantidad),
        },
      };
    });
  }, []);

  const setQty = useCallback((producto_id, qty) => {
    setItems((prev) => {
      const ex = prev[producto_id];
      if (!ex) return prev;
      const next = { ...prev };
      if (qty <= 0) delete next[producto_id];
      else next[producto_id] = { ...ex, cantidad: qty };
      return next;
    });
  }, []);

  const remove = useCallback((producto_id) => {
    setItems((prev) => {
      const next = { ...prev };
      delete next[producto_id];
      return next;
    });
  }, []);

  const clear = useCallback(() => setItems({}), []);

  const list = useMemo(() => Object.values(items), [items]);
  const count = useMemo(
    () => list.reduce((s, it) => s + Number(it.cantidad || 0), 0),
    [list]
  );
  const total = useMemo(
    () => list.reduce((s, it) => s + Number(it.cantidad || 0) * Number(it.precio || 0), 0),
    [list]
  );

  return (
    <CartContext.Provider value={{ items: list, count, total, add, setQty, remove, clear }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
